import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface NumberRange {
  min: number | null;
  max: number | null;
}

interface NumberRangeFilterProps {
  label: string;
  selectedRange: NumberRange | null;
  onFilterChange: (range: NumberRange | null) => void;
  placeholder?: string;
}

const NumberRangeFilter: React.FC<NumberRangeFilterProps> = ({
  label,
  selectedRange,
  onFilterChange,
  placeholder = 'All',
}) => {
  const { colors } = useTheme();
  const [minValue, setMinValue] = useState<string>(selectedRange?.min?.toString() || '');
  const [maxValue, setMaxValue] = useState<string>(selectedRange?.max?.toString() || '');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedRange) {
      setMinValue(selectedRange.min?.toString() || '');
      setMaxValue(selectedRange.max?.toString() || '');
    } else {
      setMinValue('');
      setMaxValue('');
    }
  }, [selectedRange]);

  const handleMinChange = (value: string) => {
    setMinValue(value);
    const min = value === '' ? null : parseFloat(value);
    const max = maxValue === '' ? null : parseFloat(maxValue);
    
    if (min === null && max === null) {
      onFilterChange(null);
    } else {
      onFilterChange({ min, max: max || null });
    }
  };

  const handleMaxChange = (value: string) => {
    setMaxValue(value);
    const min = minValue === '' ? null : parseFloat(minValue);
    const max = value === '' ? null : parseFloat(value);
    
    if (min === null && max === null) {
      onFilterChange(null);
    } else {
      onFilterChange({ min: min || null, max });
    }
  };

  const handleClear = () => {
    setMinValue('');
    setMaxValue('');
    onFilterChange(null);
  };

  const displayText = () => {
    if (!selectedRange || (selectedRange.min === null && selectedRange.max === null)) {
      return placeholder;
    }
    const parts: string[] = [];
    if (selectedRange.min !== null) {
      parts.push(`Min: ${selectedRange.min}`);
    }
    if (selectedRange.max !== null) {
      parts.push(`Max: ${selectedRange.max}`);
    }
    return parts.join(', ') || placeholder;
  };

  return (
    <div className="relative" ref={containerRef}>
      <label className={`block text-xs font-medium ${colors.textSecondary} mb-2`}>{label}</label>
      <div className={`${colors.surfaceHover} rounded-lg p-3 border ${colors.border}`}>
        <div className="flex items-center space-x-2">
          <div className="flex-1">
            <input
              type="number"
              value={minValue}
              onChange={(e) => handleMinChange(e.target.value)}
              placeholder="Min"
              className={`w-full px-3 py-2 text-sm border ${colors.border} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${colors.surface} ${colors.text} placeholder-${colors.textMuted}`}
            />
          </div>
          <span className={`text-sm ${colors.textMuted} font-medium`}>-</span>
          <div className="flex-1">
            <input
              type="number"
              value={maxValue}
              onChange={(e) => handleMaxChange(e.target.value)}
              placeholder="Max"
              className={`w-full px-3 py-2 text-sm border ${colors.border} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${colors.surface} ${colors.text} placeholder-${colors.textMuted}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NumberRangeFilter;

